// MACHINERY V2 (Stroje) — the profile tab CONTRACT (M2.4, amended M2.16d).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.4 + §5 "Catalog-vs-custom
// rule" + design_handoff_machinery_v3/README.md §3 / artboard 0504; amended by
// the mobile package's board 20 (20-profil-prehlad-live.png) and MANAGER RULING
// Q1-M2.16 below.
//
// >>> ONE PLACE DECIDES WHICH TABS EXIST. <<<
// Two consumers need that answer — MachineProfileV2 (which tab opens by
// default, and whether a remembered tab is still valid) and ProfileTabs (what
// to paint). Splitting it would be the exact "two places decide" defect the
// M2.1 card-state contract exists to prevent, one layer up.
//
// FILENAME: do NOT rename this back to `profileTabs.ts`. It would differ from
// `ProfileTabs.tsx` only in casing, which TypeScript rejects on a
// case-insensitive filesystem (TS1149) and which silently resolved the sibling
// component's import to THIS module (TS1192) before the rename.
//
// ============================================================
//  MANAGER RULING Q1-M2.16 (2026-08-25) — THE `live` TAB BECOMES `overview`
// ============================================================
// The first tab is now PREHĽAD (Overview), board 20, and three things changed
// with it. This block is the amendment record; the gate below implements it.
//
//  1. IT REPLACES THE `live` PLACEHOLDER AT ALL BREAKPOINTS, not phone-only.
//     One tab set everywhere — a desktop-vs-phone fork of the tab CONTRACT is
//     explicitly not created here, because two tab sets is two answers to the
//     question this file exists to answer once. Consequence, stated so it is
//     not discovered in testing: the DESKTOP profile changes too.
//
//  2. THE CONNECTIVITY GATE IS REMOVED. It used to read
//     `hasLiveTab(state) === state !== "unconnected"`, i.e. the tab existed only
//     when a MachineConnection row existed. Prehľad is UNGATED: the ~99% of the
//     fleet that is `unconnected` now gets the tab, showing its service-report
//     history and an honest empty state. The old rule's justification was "never
//     render a tab that is empty BY CONSTRUCTION" — that justification does not
//     survive the tab's new content, because SRs exist for machines that have no
//     gateway at all. `hasLiveTab` is therefore DELETED rather than left
//     unreferenced: a gate nobody calls is a gate somebody re-wires.
//
//  3. THE DECOMMISSIONED EXCLUSION IS KEPT, unchanged (M2.4 review ruling D: a
//     retired machine has no live surface at all — no telemetry fetch, no
//     ribbon, no tab). Only the connectivity half of the old rule is gone. That
//     half is enforced in THREE places on purpose (here, MachineProfileV2's
//     empty `machineIds`, and ProfileLiveRibbon's early return); this is the
//     first of the three and none of them may be dropped as redundant.
//
//  4. M2.10'S SIGNAL LEDGER IS PREHĽAD'S DEEP CONTENT, NOT A RIVAL TAB. When the
//     deferred ledger lands (ticket §6/§7) it mounts as a SECTION BELOW the live
//     status card inside this same tab, for connected machines only. Do not add
//     a sixth tab for it and do not restore a separate `live` tab — the reason
//     the name changed from `live` to `overview` is precisely so the ledger has
//     somewhere to live without the tab meaning two things.
//
// WHY THE MEMBER WAS RENAMED `live` -> `overview`. A key called `live` that is
// deliberately shown for machines with NO live data asserts the opposite of what
// it now gates, and a name that lies is the same class of defect as a condition
// that can never be true. Nothing persisted the old string (it is component
// state in MachineProfileV2, never written to machineryV2Persistence), so the
// rename is contained to this file, ProfileTabBodies.tsx and the i18n key.
//
// THE REMAINING GATE:
//   - Špecifikácie  only when `catalogModelId` is set. A custom machine has no
//                   catalog spec sheet to show, ever.
// A custom machine therefore lands on exactly four tabs, and a custom
// DECOMMISSIONED one on three.
//
// DEFAULT = Prehľad when it exists, else Servisné správy (M2.4 acceptance,
// unchanged in mechanism). This is a DEVIATION from the live profile, whose
// default is always `serviceReports` (data package §1) — the designer's ruling,
// carried by the ticket. It now resolves to Prehľad for nearly every machine
// rather than for the connected few, which is board 20's intent.
//
// Tab BODIES are not built here: M2.5 owns SR / Dokumenty / Špecifikácie /
// Diely, M2.16d owns Prehľad. See ProfileTabBodies.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.

/** The five profile surfaces, in board-20 order (which is also artboard 0504's
 *  order — see the reconciliation note in ProfileTabs.tsx). */
export type MachineryV2ProfileTab =
  | "overview"
  | "serviceReports"
  | "documents"
  | "specifications"
  | "parts";

/** i18next keys (myMachinery namespace). v2 carries its OWN tab vocabulary —
 *  the designer labels the surfaces Dokumenty / Diely a materiál, where the
 *  live profile says Manuals & Software / Parts and Consumables. One block, so
 *  the wording cannot drift tab-to-tab. */
export const PROFILE_TAB_LABEL_KEYS: Record<MachineryV2ProfileTab, string> = {
  overview: "myMachinery:v2.profile.tabs.overview",
  serviceReports: "myMachinery:v2.profile.tabs.serviceReports",
  documents: "myMachinery:v2.profile.tabs.documents",
  specifications: "myMachinery:v2.profile.tabs.specifications",
  parts: "myMachinery:v2.profile.tabs.parts",
};

export interface ProfileTabContext {
  /** `!!machine.catalogModelId` — the catalog-vs-custom discriminator. */
  hasCatalogModel: boolean;
  /**
   * `machine.isDecommissioned === true`. A retired machine loses the Prehľad tab
   * OUTRIGHT (ruling D) rather than incidentally: MachineProfileV2 already
   * fetches it no telemetry, so a derived state would read `unconnected`
   * anyway — but "no live surface for a retired machine" is a RULE, and a rule
   * enforced only as a side effect of another rule breaks silently the first
   * time telemetry arrives from somewhere else.
   */
  decommissioned: boolean;
}

/**
 * The ordered tab set for one machine.
 *
 * NOTE WHAT IS NO LONGER AN INPUT: the M2.1 card state. Q1-M2.16 removed the
 * connectivity gate, so the derived state stops being a tab-visibility input
 * altogether. It is deliberately NOT kept as an ignored parameter — an argument
 * every caller must still compute and that nothing reads is the shape a gate
 * silently grows back into.
 */
export const buildProfileTabs = (ctx: ProfileTabContext): MachineryV2ProfileTab[] => {
  const tabs: MachineryV2ProfileTab[] = [];
  if (!ctx.decommissioned) tabs.push("overview");
  tabs.push("serviceReports", "documents");
  if (ctx.hasCatalogModel) tabs.push("specifications");
  tabs.push("parts");
  return tabs;
};

/** Prehľad when present, else Servisné správy. */
export const defaultProfileTab = (
  tabs: MachineryV2ProfileTab[]
): MachineryV2ProfileTab =>
  tabs.indexOf("overview") >= 0 ? "overview" : "serviceReports";

/**
 * The tab actually rendered: the user's pick while it remains valid, else the
 * default.
 *
 * THE FALLBACK IS STILL NOT COSMETIC, though Q1-M2.16 changed which case it
 * covers. It used to absorb the tick between the machine arriving and its
 * telemetry arriving (the tab set grew a Live tab one render later) and the
 * reverse drop-off. With the connectivity gate gone, the tab set no longer moves
 * under telemetry at all; what remains is the DECOMMISSION transition — restore
 * or decommission from the profile refreshes the machine, the Prehľad tab
 * appears or vanishes, and a chosen tab that no longer exists must fall back
 * rather than render an empty panel.
 */
export const resolveProfileTab = (
  tabs: MachineryV2ProfileTab[],
  chosen: MachineryV2ProfileTab | null
): MachineryV2ProfileTab =>
  chosen && tabs.indexOf(chosen) >= 0 ? chosen : defaultProfileTab(tabs);
